/**
 * Trust & Safety — Phase 1 · TechSprint Hero Journey
 *
 * Landing rebuilt around "Check something suspicious".
 *   • Three evidence tabs: Message / Screenshot, Link / Website, Document
 *   • Platform selector chips (WhatsApp · SMS · Email · TikTok · Facebook ·
 *     Instagram · Web · Phone call · Other)
 *   • Optional context — "Who does this claim to be from?"
 *   • Runs a Trust Check that compares the evidence against the consumer's
 *     My Trusted Institutions record and returns a 4-section verdict:
 *       Verify these first · Doesn't match · Couldn't verify · Look closer
 *   • Retains: Altara Trust Certificate verifier (VerifyDocumentCard) +
 *     "I think I've been scammed" emergency support.
 *
 * Deterministic Phase 1 scenario:
 *   Nomsa sees a TikTok advert for ABC Wealth → gets a WhatsApp from
 *   someone claiming to be "James Smith" with new payment details / urgency
 *   → Trust Check flags impersonation because the trusted-institution
 *   record contradicts the message.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  ShieldAlert, ShieldCheck, AlertTriangle, Sparkles, Phone, Ban, Loader2,
  Upload, X, FileText, ExternalLink, ArrowRight, MessageSquare, Link2,
  FileWarning, Search, CheckCircle2, XCircle, HelpCircle, Eye, RefreshCw,
} from "lucide-react";
import { emitSignal } from "@/lib/wallet-signal";
import { getDeviceId } from "@/lib/drop-off-notifications";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const API_TRUST = `${process.env.REACT_APP_BACKEND_URL || ""}/api/trust`;
const API_TS = `${API}/wallet/trust-safety`;

const PLATFORMS = [
  { id: "whatsapp",   label: "WhatsApp" },
  { id: "sms",        label: "SMS" },
  { id: "email",      label: "Email" },
  { id: "tiktok",     label: "TikTok" },
  { id: "facebook",   label: "Facebook" },
  { id: "instagram",  label: "Instagram" },
  { id: "web",        label: "Website" },
  { id: "phone_call", label: "Phone call" },
  { id: "other",      label: "Other" },
];

export default function WalletSafety() {
  return (
    <WalletShell activeTab="safety">
      <div className="max-w-6xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-10" data-testid="wallet-safety">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Section</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>Trust &amp; Safety</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            AI-driven impersonation scams are the number-one threat facing consumers today.
            Bring us anything suspicious — a message, a link, a document — and we&apos;ll compare
            it against your trusted-institution record and tell you what to trust, what
            doesn&apos;t match, and what needs a closer look.
          </p>
        </header>

        {/* HERO — Check something suspicious */}
        <TrustCheckWizard />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2"><VerifyDocumentCard /></div>
          <EmergencyHelpCard />
        </div>

        <SafetyTipsCard />
      </div>
    </WalletShell>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Trust Check Wizard — the new Phase 1 hero flow
// ═════════════════════════════════════════════════════════════════════

const EVIDENCE_TABS = [
  { id: "message",  label: "Message / Screenshot", icon: MessageSquare,
    hint: "Paste the SMS / WhatsApp / email you received." },
  { id: "link",     label: "Link / Website",       icon: Link2,
    hint: "Paste any URL to check the destination." },
  { id: "document", label: "Document",             icon: FileWarning,
    hint: "Drop a document that claims to be from your institution." },
];

function TrustCheckWizard() {
  const [tab, setTab] = useState("message");
  const [platform, setPlatform] = useState("whatsapp");
  const [messageText, setMessageText] = useState("");
  const [screenshotName, setScreenshotName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [docName, setDocName] = useState("");
  const [docSha, setDocSha] = useState("");
  const [claimedInstitution, setClaimedInstitution] = useState("");
  const [claimedPerson, setClaimedPerson] = useState("");
  const [context, setContext] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_TS}/trusted-context`, { params: { device_id: getDeviceId() } });
        if (ok) setContext(data);
      } catch { /* silent — landing still works */ }
    })();
    return () => { ok = false; };
  }, []);

  const canSubmit = (() => {
    if (busy) return false;
    if (tab === "message") return messageText.trim().length > 0 || screenshotName;
    if (tab === "link") return linkUrl.trim().length > 0;
    if (tab === "document") return !!docName;
    return false;
  })();

  const reset = () => {
    setResult(null); setError("");
    setMessageText(""); setScreenshotName("");
    setLinkUrl(""); setDocName(""); setDocSha("");
    setClaimedInstitution(""); setClaimedPerson("");
  };

  const onScreenshotChosen = (f) => {
    if (!f) return;
    setScreenshotName(f.name);
  };

  const onDocChosen = async (f) => {
    if (!f) return;
    setDocName(f.name);
    try {
      const buf = await f.arrayBuffer();
      const hashBuf = await crypto.subtle.digest("SHA-256", buf);
      const sha = [...new Uint8Array(hashBuf)].map(b => b.toString(16).padStart(2, "0")).join("");
      setDocSha(sha);
    } catch { /* ignore */ }
  };

  const submit = async () => {
    setBusy(true); setError(""); setResult(null);
    const evidence = {
      evidence_type: tab,
      platform,
      message_text: tab === "message" ? messageText : undefined,
      screenshot_filename: tab === "message" && screenshotName ? screenshotName : undefined,
      link_url: tab === "link" ? linkUrl : undefined,
      document_filename: tab === "document" ? docName : undefined,
      document_sha256: tab === "document" && docSha ? docSha : undefined,
      claimed_institution: claimedInstitution || undefined,
      claimed_person: claimedPerson || undefined,
    };
    try {
      const { data } = await axios.post(`${API_TS}/trust-check`, {
        device_id: getDeviceId(),
        evidence,
      });
      setResult(data);
      emitSignal("trust_check.submitted.v1", {
        evidence_type: tab,
        platform,
        band: data.overall_band,
        doesnt_match_count: data.doesnt_match.length,
        verify_first_count: data.verify_these_first.length,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Trust check failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl overflow-hidden border shadow-sm bg-white"
             style={{ borderColor: `${GOLD}55` }}
             data-testid="trust-check-wizard">
      <div className="px-6 py-5 text-white flex items-center gap-3" style={{ background: NAVY }}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: GOLD }}>
          <Search className="w-5 h-5" style={{ color: NAVY }} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Trust Check</div>
          <div className="text-lg font-semibold">Check something suspicious</div>
        </div>
      </div>

      {!result ? (
        <div className="p-6 space-y-6">
          {/* Trusted-institution anchor callout */}
          {context?.institution && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4"
                 data-testid="trusted-anchor">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-emerald-800">
                <ShieldCheck className="w-3.5 h-3.5" /> We&apos;ll check against your trusted record
              </div>
              <div className="mt-1 text-[13px] text-slate-700">
                <strong style={{ color: NAVY }}>{context.institution.trading_name}</strong>
                {context.representative && (
                  <> · adviser <strong style={{ color: NAVY }}>{context.representative.name}</strong></>
                )}
                <> · {context.channels?.length || 0} official channels on record</>
              </div>
            </div>
          )}

          {/* Evidence tabs */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
              1 · What are you checking?
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="evidence-tabs">
              {EVIDENCE_TABS.map(t => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { setTab(t.id); setError(""); }}
                    data-testid={`evidence-tab-${t.id}`}
                    className="rounded-xl border p-3 text-left transition"
                    style={active
                      ? { borderColor: NAVY, background: `${NAVY}0A`, boxShadow: `inset 0 0 0 1px ${NAVY}` }
                      : { borderColor: "#E2E8F0", background: "white" }}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: active ? NAVY : "#64748B" }} />
                      <div className="text-[13px] font-semibold" style={{ color: active ? NAVY : "#0F172A" }}>
                        {t.label}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">{t.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Platform selector */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
              2 · Where did it arrive?
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="platform-selector">
              {PLATFORMS.map(p => {
                const active = platform === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlatform(p.id)}
                    data-testid={`platform-${p.id}`}
                    className="px-3 py-1.5 rounded-full text-[12px] font-medium border transition"
                    style={active
                      ? { background: NAVY, borderColor: NAVY, color: "white" }
                      : { background: "white", borderColor: "#E2E8F0", color: "#334155" }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Evidence input area */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
              3 · The evidence
            </div>

            {tab === "message" && (
              <div className="space-y-3" data-testid="evidence-input-message">
                <textarea
                  value={messageText}
                  onChange={e => setMessageText(e.target.value)}
                  rows={5}
                  data-testid="message-text-input"
                  placeholder="Paste the exact wording of the message here…"
                  className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:border-slate-400 resize-none"
                />
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer hover:text-slate-800"
                         data-testid="screenshot-upload-label">
                    <Upload className="w-3.5 h-3.5" />
                    Add a screenshot (optional)
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="screenshot-upload-input"
                      onChange={e => onScreenshotChosen(e.target.files?.[0])}
                    />
                  </label>
                  {screenshotName && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 rounded-full px-2 py-0.5"
                          data-testid="screenshot-name">
                      <FileText className="w-3 h-3" /> {screenshotName}
                      <button type="button" onClick={() => setScreenshotName("")} className="text-slate-400 hover:text-rose-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}

            {tab === "link" && (
              <div data-testid="evidence-input-link">
                <input
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  data-testid="link-url-input"
                  placeholder="https://…"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-400"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  We check the domain against your trusted institution&apos;s official website.
                </div>
              </div>
            )}

            {tab === "document" && (
              <label
                data-testid="evidence-input-document"
                className="block rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition hover:border-slate-300"
                style={{ borderColor: docName ? "#6EE7B7" : "#E2E8F0", background: docName ? "#ECFDF5" : "white" }}
              >
                {docName ? (
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium" style={{ color: NAVY }}>{docName}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setDocName(""); setDocSha(""); }}
                      className="text-slate-400 hover:text-rose-600 ml-2"
                    ><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-6 h-6 mx-auto text-slate-400" />
                    <div className="mt-2 text-sm text-slate-600">
                      Drop the document or <span className="underline" style={{ color: NAVY }}>choose a file</span>
                    </div>
                    <div className="mt-1 text-[10px] text-slate-500">
                      Hashed locally in your browser · never uploaded.
                    </div>
                  </>
                )}
                <input
                  type="file"
                  data-testid="document-upload-input"
                  className="hidden"
                  onChange={e => onDocChosen(e.target.files?.[0])}
                />
              </label>
            )}
          </div>

          {/* Claimed identity */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
              4 · Who does it claim to be? <span className="text-slate-400 normal-case">(optional but recommended)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                value={claimedInstitution}
                onChange={e => setClaimedInstitution(e.target.value)}
                data-testid="claimed-institution-input"
                placeholder="Institution (e.g. ABC Wealth)"
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
              />
              <input
                value={claimedPerson}
                onChange={e => setClaimedPerson(e.target.value)}
                data-testid="claimed-person-input"
                placeholder="Person (e.g. James Smith)"
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
              />
            </div>
          </div>

          {error && (
            <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3"
                 data-testid="trust-check-error">{error}</div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center pt-2">
            <button
              type="button"
              disabled={!canSubmit}
              onClick={submit}
              data-testid="run-trust-check"
              className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white transition disabled:opacity-40"
              style={{ background: NAVY }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {busy ? "Running Trust Check…" : "Run Trust Check"}
            </button>
            <button
              type="button"
              onClick={reset}
              className="text-[12px] text-slate-500 hover:text-slate-700"
              data-testid="reset-trust-check"
            >Reset</button>
            <div className="text-[11px] text-slate-500 sm:ml-auto">
              We never contact senders on your behalf — you stay in control.
            </div>
          </div>
        </div>
      ) : (
        <TrustCheckResults result={result} onReset={reset} />
      )}
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Trust Check Results — 4-section verdict view
// ═════════════════════════════════════════════════════════════════════

const BAND_STYLE = {
  high:    { color: "#9F1239", bg: "#FEE2E2", ring: "#FCA5A5", chip: "High risk",  Icon: XCircle },
  caution: { color: "#B45309", bg: "#FEF3C7", ring: "#FCD34D", chip: "Take care",  Icon: AlertTriangle },
  low:     { color: "#065F46", bg: "#D1FAE5", ring: "#6EE7B7", chip: "Low risk",   Icon: CheckCircle2 },
};

const SECTION_CONFIG = [
  { key: "verify_these_first",
    title: "Verify these first",
    subtitle: "Anchors from your trusted-institution record.",
    Icon: ShieldCheck,
    tone: { color: "#065F46", bg: "white", ring: "#6EE7B7" } },
  { key: "doesnt_match",
    title: "Doesn't match your record",
    subtitle: "Contradicts what your real institution does.",
    Icon: XCircle,
    tone: { color: "#9F1239", bg: "white", ring: "#FCA5A5" } },
  { key: "couldnt_verify",
    title: "Couldn't verify",
    subtitle: "Transparent unknowns — confirm through official channels.",
    Icon: HelpCircle,
    tone: { color: "#334155", bg: "white", ring: "#CBD5E1" } },
  { key: "look_closer",
    title: "Look closer",
    subtitle: "Patterns worth extra scrutiny.",
    Icon: Eye,
    tone: { color: "#B45309", bg: "white", ring: "#FCD34D" } },
];

function TrustCheckResults({ result, onReset }) {
  const band = BAND_STYLE[result.overall_band] || BAND_STYLE.caution;
  const BandIcon = band.Icon;
  return (
    <div className="p-6 space-y-6" data-testid="trust-check-results">
      {/* Overall verdict */}
      <div className="rounded-2xl p-5 border"
           style={{ background: band.bg, borderColor: band.ring }}
           data-testid={`trust-check-band-${result.overall_band}`}>
        <div className="flex items-start gap-3">
          <BandIcon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: band.color }} />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: band.color }}>
              {band.chip}
            </div>
            <div className="text-lg font-semibold mt-0.5" style={{ color: band.color }}>
              {result.headline}
            </div>
            <div className="text-[13px] mt-2 text-slate-700 leading-relaxed">{result.summary}</div>
          </div>
        </div>
      </div>

      {/* 4-section verdict */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SECTION_CONFIG.map(section => (
          <VerdictSection
            key={section.key}
            testId={`verdict-${section.key.replace(/_/g, "-")}`}
            title={section.title}
            subtitle={section.subtitle}
            Icon={section.Icon}
            tone={section.tone}
            items={result[section.key] || []}
          />
        ))}
      </div>

      {/* Trusted contact anchor */}
      {result.trusted_institution && (
        <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50/50"
             data-testid="results-trusted-anchor">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Verified channels for {result.trusted_institution.trading_name}
          </div>
          <div className="text-[13px] text-slate-700">
            {result.trusted_representative && (
              <span>Adviser <strong style={{ color: NAVY }}>{result.trusted_representative.name}</strong> · </span>
            )}
            <span className="text-slate-500">
              Manage in <a href="/wallet/institutions" className="underline" style={{ color: NAVY }}
                data-testid="results-open-institutions">My Trusted Institutions</a>.
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          data-testid="check-another"
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white"
          style={{ background: NAVY }}
        ><RefreshCw className="w-4 h-4" /> Check something else</button>
        <a
          href="#emergency"
          className="text-[12px] font-medium text-rose-700 hover:underline ml-auto"
          data-testid="results-jump-emergency"
        >Already been scammed? Get emergency help ›</a>
      </div>
    </div>
  );
}

function VerdictSection({ title, subtitle, Icon, tone, items, testId }) {
  return (
    <div className="rounded-2xl border p-4 bg-white shadow-sm"
         style={{ borderColor: tone.ring }} data-testid={testId}>
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: tone.color }} />
        <div>
          <div className="text-sm font-semibold" style={{ color: tone.color }}>{title}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
        </div>
      </div>
      <div className="mt-3">
        {items.length === 0 ? (
          <div className="text-[12px] text-slate-400 italic">Nothing to show here.</div>
        ) : (
          <ul className="space-y-2.5">
            {items.map((it, i) => (
              <li key={i} className="border-l-2 pl-3" style={{ borderColor: tone.ring }}>
                <div className="text-[13px] font-medium text-slate-800">{it.label}</div>
                <div className="text-[12px] text-slate-600 leading-relaxed mt-0.5">{it.detail}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Verify an Altara-issued document (Trust Certificate lookup) — retained
// ═════════════════════════════════════════════════════════════════════

function VerifyDocumentCard() {
  const [mode, setMode] = useState("id"); // "id" | "file"
  return (
    <div className="rounded-2xl overflow-hidden border shadow-sm bg-white"
         style={{ borderColor: `${GOLD}55` }} data-testid="verify-document-card">
      <div className="px-5 py-4 text-white flex items-center gap-3" style={{ background: NAVY }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: GOLD }}>
          <ShieldCheck className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Verify an Altara certificate</div>
          <div className="text-[15px] font-semibold">Is this document really Altara-verified?</div>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-[13px] leading-relaxed text-slate-700">
          Every document Altara delivers into your wallet carries a signed <strong style={{ color: NAVY }}>Altara Trust Certificate</strong>.
          Look up any certificate by ID, or drop the file you received.
        </p>

        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" data-testid="verify-mode-toggle">
          <ModeButton active={mode === "id"}   onClick={() => setMode("id")}   testId="verify-mode-id">By certificate ID</ModeButton>
          <ModeButton active={mode === "file"} onClick={() => setMode("file")} testId="verify-mode-file">By file</ModeButton>
        </div>

        {mode === "id" ? <VerifyByIdPanel /> : <VerifyByFilePanel />}

        <div className="pt-3 mt-4 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
          <span>Signed with Ed25519 · verifiable offline</span>
          <a href={`${API_TRUST}/public-key`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 hover:underline" style={{ color: NAVY }}
             data-testid="safety-view-jwks">
            View Altara&apos;s public key <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}

function ModeButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="px-3 py-1.5 text-[12px] font-medium rounded-md transition"
      style={active
        ? { background: "white", color: NAVY, boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }
        : { background: "transparent", color: "#64748B" }}
    >{children}</button>
  );
}

function VerifyByIdPanel() {
  const [id, setId] = useState("");
  const [error, setError] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const trimmed = id.trim().toUpperCase();
    if (!/^ATC-[0-9A-F]{12}$/.test(trimmed)) {
      setError("Certificate IDs look like ATC- followed by 12 letters/numbers.");
      return;
    }
    setError("");
    window.open(`/verify/${trimmed}`, "_blank", "noreferrer");
  };
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="verify-by-id-panel">
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Certificate ID</label>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={id}
          onChange={e => { setId(e.target.value); setError(""); }}
          placeholder="ATC-XXXXXXXXXXXX"
          data-testid="verify-id-input"
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono uppercase tracking-wider focus:outline-none focus:border-slate-400"
        />
        <button
          type="submit"
          data-testid="verify-id-submit"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white"
          style={{ background: NAVY }}
        ><ShieldCheck className="w-4 h-4" /> Open certificate</button>
      </div>
      {error && <div className="text-[11px] text-rose-600" data-testid="verify-id-error">{error}</div>}
    </form>
  );
}

function VerifyByFilePanel() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const runLookup = async (f) => {
    setBusy(true); setError(""); setResult(null);
    try {
      const buffer = await f.arrayBuffer();
      const hashBuf = await crypto.subtle.digest("SHA-256", buffer);
      const sha = [...new Uint8Array(hashBuf)].map(b => b.toString(16).padStart(2, "0")).join("");
      const { data } = await axios.post(`${API_TRUST}/certificates/lookup-by-sha?sha256=${sha}`);
      setResult({ sha, ...data });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="verify-by-file-panel">
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={async e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) { setFile(f); await runLookup(f); } }}
        onClick={() => document.getElementById("safety-verify-file-input").click()}
        data-testid="verify-file-dropzone"
        className={`rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition ${
          dragOver ? "border-slate-400 bg-slate-50" :
          file ? "border-emerald-300 bg-emerald-50" :
          "border-slate-200 hover:border-slate-300"
        }`}
      >
        {file ? (
          <div className="flex items-center justify-center gap-2 text-sm">
            <FileText className="w-4 h-4 text-emerald-600" />
            <span className="font-medium" style={{ color: NAVY }}>{file.name}</span>
            <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); }} className="text-slate-400 hover:text-rose-600 ml-2"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <>
            <Upload className="w-6 h-6 mx-auto text-slate-400" />
            <div className="mt-2 text-sm text-slate-600">Drop file here or <span className="underline" style={{ color: NAVY }}>choose a file</span></div>
            <div className="mt-1 text-[10px] text-slate-500">Hashed locally in your browser — never uploaded.</div>
          </>
        )}
        <input
          id="safety-verify-file-input"
          type="file"
          data-testid="verify-file-input"
          className="hidden"
          onChange={async e => { const f = e.target.files?.[0]; if (f) { setFile(f); await runLookup(f); } }}
        />
      </div>

      {busy && (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Hashing &amp; checking…</div>
      )}
      {error && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="verify-file-error">{error}</div>}
      {result && <VerifyFileResult result={result} />}
    </div>
  );
}

function VerifyFileResult({ result }) {
  if (result.found) {
    const c = result.certificates[0];
    return (
      <div className="rounded-2xl border p-4 bg-emerald-50 border-emerald-200 space-y-3" data-testid="verify-file-found">
        <div className="flex items-center gap-2 font-semibold" style={{ color: "#065F46" }}>
          <ShieldCheck className="w-5 h-5" /> This is a genuine Altara-Verified document
        </div>
        <div className="text-[12px] text-slate-700 leading-relaxed">
          The file&apos;s SHA-256 matches {result.match_count === 1 ? "one certificate" : `${result.match_count} certificates`} in Altara&apos;s registry.
        </div>
        <div className="rounded-xl bg-white border border-emerald-200 p-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: GOLD }}>
            <FileText className="w-4 h-4" style={{ color: NAVY }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest" style={{ color: NAVY }}>Certificate</div>
            <div className="font-mono text-[13px] font-semibold break-all" style={{ color: NAVY }}>{c.certificate_id}</div>
            <div className="text-[11px] text-slate-600 mt-1">
              {c.depositor_institution || "Institution"} · issued {new Date(c.issued_at).toLocaleDateString()}
              {c.is_latest ? " · latest revision" : " · superseded"}
            </div>
          </div>
          <a
            href={c.verify_url || `/verify/${c.certificate_id}`}
            target="_blank"
            rel="noreferrer"
            data-testid="verify-file-open-cert"
            className="inline-flex items-center gap-1 text-[12px] font-medium hover:underline flex-shrink-0"
            style={{ color: NAVY }}
          >Open <ArrowRight className="w-3 h-3" /></a>
        </div>
        <div className="font-mono text-[10px] text-slate-500 break-all bg-white/60 p-2 rounded">SHA-256 · {result.sha}</div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border p-4 bg-rose-50 border-rose-200 space-y-2" data-testid="verify-file-not-found">
      <div className="flex items-center gap-2 font-semibold" style={{ color: "#9F1239" }}>
        <AlertTriangle className="w-5 h-5" /> No matching certificate found
      </div>
      <div className="text-[12px] text-slate-700 leading-relaxed">
        This file was <strong>not</strong> delivered through Altara. Contact the sender through an official channel before opening or acting on it.
      </div>
      <div className="font-mono text-[10px] text-slate-500 break-all bg-white/60 p-2 rounded">SHA-256 · {result.sha}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Emergency help + Safety tips — retained
// ═════════════════════════════════════════════════════════════════════

function EmergencyHelpCard() {
  return (
    <div id="emergency" className="rounded-2xl border border-rose-200 shadow-sm p-5" style={{ background: "#FEE2E2" }} data-testid="emergency-help">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-rose-600"><Phone className="w-4 h-4 text-white" /></div>
        <div>
          <div className="text-sm font-semibold text-rose-900">I think I&apos;ve been scammed</div>
          <div className="text-[11px] text-rose-700">Immediate steps to protect yourself.</div>
        </div>
      </div>
      <ol className="mt-3 space-y-1.5 list-decimal pl-4 text-[13px] text-rose-900">
        <li>Call your bank on the number on the back of your card — <strong>right now</strong>.</li>
        <li>Freeze your card via the bank app.</li>
        <li>Do not click, reply or share anything else.</li>
        <li>Report to SAPS and the FSCA scam register.</li>
      </ol>
      <div className="mt-4 flex flex-wrap gap-2">
        <a href="tel:10111" data-testid="emergency-saps" className="inline-flex items-center gap-1 rounded-lg bg-rose-600 text-white text-xs font-medium px-3 py-1.5"><Phone className="w-3 h-3" /> SAPS 10111</a>
        <a href="https://www.sabric.co.za" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg bg-white text-rose-900 border border-rose-300 text-xs font-medium px-3 py-1.5"><Ban className="w-3 h-3" /> SABRIC</a>
      </div>
    </div>
  );
}

function SafetyTipsCard() {
  const tips = [
    "Legitimate banks NEVER ask for your OTP, PIN, or card number by SMS or WhatsApp.",
    "If a message pressures urgency (\"within 24 hours\", \"account will be closed\"), it's almost certainly a scam.",
    "Type your bank's URL directly — never click links from messages.",
    "If someone calls claiming to be your bank, hang up and call back on the official number.",
    "Take a screenshot before you delete — proof helps SAPS and SABRIC investigations.",
  ];
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5" data-testid="safety-tips">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#10B98118" }}><ShieldCheck className="w-4 h-4 text-emerald-600" /></div>
        <div className="text-sm font-semibold" style={{ color: NAVY }}>Safe banking habits</div>
      </div>
      <ul className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
        {tips.map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700"><Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />{t}</li>
        ))}
      </ul>
    </div>
  );
}
